import { Checklist, ChecklistOutlined, ExpandMore, LocalShippingSharp, ManageSearch, InfoOutlined } from "@mui/icons-material";
import { Button, Card, Alert, Accordion, AccordionSummary, Typography, AccordionDetails, LinearProgress, Chip, TablePagination, Box, FormControl, Select, MenuItem, InputLabel, FormHelperText, Tooltip, IconButton, CircularProgress } from "@mui/material";
import axios, { AxiosResponse } from "axios";
import { formatDate } from "date-fns";
import { useState, useContext, useEffect } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { DraftFunction, useImmer } from "use-immer";
import { API_ENDPOINT } from "../../../../../configurations/global.config";
import { useSnackBar } from "../../../../../contexts/snackbar";
import { AuthContext } from "../../../../../contexts/user-context/user-context";
import { OrderDeliveryTaskState } from "../../../../../enums/tasks";
import { DriverInvoicePurpose, DriverInvoiceType } from "../../../../../interfaces/common/driver-invoices";
import { DriverType } from "../../../../../interfaces/delivery-company/drivers";
import { IOrderDeliveryTask, IPickupTask } from "../../../../../interfaces/delivery-company/tasks";
import { get_cod } from "../../../../../utils/cod";
import { displayLocation } from "../../../../../utils/locations";
import { displayMoney, Money } from "../../../../../utils/money";
import UpdateCommissionDialog from "../../../../common/delivery-task-commission/update-commission-dialog";
import DoubleDateOrdersFilter from "../../../../common/double-date-orders-filter/double-date-orders-filter";
import { generateDateFilterQueryParam } from "../../../../common/double-date-orders-filter/utils";
import { DoubleDateWithMode, DoubleDateMode } from "../../../../common/double-date-picker/double-date-picker.interfaces";
import { ErrorsFlattenner } from "../../../../common/errors-flattenner/errors-flattenner";
import FilterBar from "../../../../common/filter-bar/filter-bar";
import { buildQueryParams } from "../../../../common/filter-bar/filter-bar.utils";
import { IFilterOption } from "../../../../common/filter-bar/types";
import GenericTable from "../../../../common/generic-table/generic-table";
import IncompleteAddressError from "../../../../common/incomplete-address-error/incomplete-address-error";
import OrderNotes from "../../../../common/order-notes/order-notes";
import { showWorkflow } from "../../../../common/styles/workflow-display";
import DriverAmountsSummary from "../driver-amounts-summary";
import { Workflow } from "../../../../../enums/orders";
import Grid2 from "@mui/material/Unstable_Grid2";
import InvoiceableDeliveryTasks from "./invoiceable-delivery-tasks";
import TabsLayout from "../../../../common/tabs-layout/tabs-layout";
import InvoiceablePickupTasks from "./invoiceable-pickup-tasks";


interface CreateNewDriverInvoiceProps {
  driver: DriverType;
  onSuccess: () => void;
}

function CreateNewDriverInvoice({
  driver,
  onSuccess
}: CreateNewDriverInvoiceProps) {

  const [message, setMessage] = useState('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [filters, setFilters] = useState<IFilterOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {showMessage} = useSnackBar()


  const [selectedDeliveryTasks, updateSelectedDeliveryTasks] = useImmer<Map<string, IOrderDeliveryTask>>(
    new Map()
  );
  const [selectedPickupTasks, updateSelectedPickupTasks] = useImmer<Map<string, IPickupTask>>(
    new Map()
  );

  // Invoice purpose state
  const settlementMode = driver.effective_settlement_mode ?? 'NET';
  const [invoicePurpose, setInvoicePurpose] = useState<DriverInvoicePurpose>(
    settlementMode === 'NET' ? DriverInvoicePurpose.NET : DriverInvoicePurpose.COLLECTION
  );

  const totalSelected = selectedDeliveryTasks.size + selectedPickupTasks.size

  const { config } = useContext(AuthContext);


  const handleGenerateInvoice = async () => {
    setIsLoading(true);
    setResponse(undefined);
    setMessage('');
    try {
        const endpoint = `${API_ENDPOINT}/v1/driver-invoices/`
        const data = {
            driver: driver.id,
            delivery_tasks_keys:  Array.from(selectedDeliveryTasks.values()).map(item => item.id),
            pickup_tasks_keys: Array.from(selectedPickupTasks.values()).map(item => item.id),
            purpose: invoicePurpose,
        }
        console.log(data)
        await axios.post(endpoint, data, config)
        onSuccess()
    } catch (e) {
      let message = 'Failed to create invoice';
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        message = `${message}: ${e.response?.statusText}`;
      }
      setMessage(message);
    }

    setIsLoading(false);
  };

  return (
    <div>
      <Box display={"flex"} gap={3} alignItems={"center"} >

          {/* Invoice Purpose Selector - Compact Version */}
          <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 280 }}>
            <InputLabel id="invoice-purpose-label">Invoicing Mode</InputLabel>
            <Select
              labelId="invoice-purpose-label"
              value={invoicePurpose}
              label="Invoice Purpose"
              onChange={(e) => setInvoicePurpose(e.target.value as DriverInvoicePurpose)}
            >
              <MenuItem value="NET">
                <Box>
                  <Typography variant="body2"><strong>Net Settlement</strong></Typography>
                  <Typography variant="caption" color="text.secondary">
                    Driver pays collected amounts minus commission.
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="COLLECTION">
                <Box>
                  <Typography variant="body2"><strong>Collection Invoice</strong></Typography>
                  <Typography variant="caption" color="text.secondary">
                    Driver pays collected amounts.
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="COMMISSION">
                <Box>
                  <Typography variant="body2"><strong>Commission Invoice</strong></Typography>
                  <Typography variant="caption" color="text.secondary">
                    Company pays driver commission.
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
            <FormHelperText>
              Default: {settlementMode === 'NET' ? 'Net settlement' : 'Gross settlement'}
            </FormHelperText>
          </FormControl>

          

          {totalSelected > 0 && <Box maxWidth={800}  margin={"auto"} mb={2} >
              <DriverAmountsSummary  
                deliveryTaskIds={Array.from(selectedDeliveryTasks.keys()).map(id => Number(id))} 
                pickupTaskIds={Array.from(selectedPickupTasks.keys()).map(id => Number(id))}
                driverId={Number(driver.id)}
                purpose={invoicePurpose}
              />

              </Box>
            }

      </Box>


       

      <Card sx={{ padding: 2, mt: 1 }} elevation={0}>
        {message !== '' && (
          <>
            <Alert
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
            >
              <>
                {message}

                {response && response.status >= 300 && (
                  <ErrorsFlattenner rawErrors={response.data} />
                )}
              </>
            </Alert>

            <br />
          </>
        )}
      </Card>

      <Box>
            <Box display={"flex"} justifyContent={"center"} gap={2}>
                  <Chip icon={<ChecklistOutlined />} label={`${selectedDeliveryTasks.size} Delivery Tasks`}></Chip> 
                  <Chip icon={<ChecklistOutlined />} label={`${selectedPickupTasks.size} Pickup Tasks`}></Chip> 
              </Box>

              <Box width={"100%"} display={"flex"} justifyContent={"center"} mt={2} mb={1}>
                  {isLoading ? <CircularProgress size={20} /> : <Button
                    size={(isMobile && !isTablet) ? 'small' : 'medium'}
                    disabled={totalSelected === 0 || isLoading}
                    variant="contained"
                    onClick={handleGenerateInvoice}
                  >
                    Create Invoice
                  </Button>}
              </Box>
          </Box>

      
       


      

      <TabsLayout
        orientation="horizontal"
        persistOnSwitch
        source="driver-invoice-form"
        sections={[
          {
            label: 'Delivery',
            icon: <LocalShippingSharp />,
            content: <InvoiceableDeliveryTasks 
                        driver={driver} 
                        selectedItems={selectedDeliveryTasks} 
                        updateSelectedItems={updateSelectedDeliveryTasks}
                        invoicePurpose={invoicePurpose}
                    />
          },
          {
            label: 'Pickup',
            icon: <LocalShippingSharp />,
            content: <InvoiceablePickupTasks 
                        driver={driver} 
                        selectedItems={selectedPickupTasks} 
                        updateSelectedItems={updateSelectedPickupTasks}
                        invoicePurpose={invoicePurpose}
                    />
          },
        ]}
      />

    </div>
  );
}

export default CreateNewDriverInvoice;
